import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import {IconAlertCircle, IconCheck} from '@tabler/icons-react';
import type {FC} from 'react';
import {
  assistantToolLabel,
  humanizeToolName,
} from 'src/app/assistant/assistantToolLabels';
import {useAssistantQueryCsvDownload} from 'src/app/assistant/useAssistantQueryCsvDownload';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {ExportIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';

export interface ToolCallRowDownload {
  conversationId: number;
  toolUseId: string;
  /** 1-based position among the reply's query rows, for the accessible name. */
  ordinal: number;
}

export interface ToolCallRowProps {
  tool: string;
  running?: boolean;
  isError?: boolean;
  /** A saved query row's CSV download; omitted while streaming. */
  download?: ToolCallRowDownload;
}

const QueryCsvAction: FC<ToolCallRowDownload> = ({
  conversationId,
  toolUseId,
  ordinal,
}) => {
  const {download, downloading} = useAssistantQueryCsvDownload(
    conversationId,
    toolUseId
  );
  return (
    <ActionIcon
      size="xs"
      color="gray"
      tooltipText={`Download query ${ordinal} as CSV`}
      loading={downloading}
      onClick={() => void download()}>
      <ExportIcon size={IconSize.sm} />
    </ActionIcon>
  );
};

export const ToolCallRow: FC<ToolCallRowProps> = ({
  tool,
  running = false,
  isError = false,
  download,
}) => (
  <Group gap="xs">
    {running ? (
      <Loader size="xs" type="dots" color="gray" />
    ) : isError ? (
      <IconAlertCircle size={14} color="var(--mantine-color-red-6)" />
    ) : (
      <IconCheck size={14} color="var(--mantine-color-dimmed)" />
    )}
    <Text size="sm" c="dimmed">
      {isError
        ? `Lookup failed: ${humanizeToolName(tool)}`
        : assistantToolLabel(tool, running)}
    </Text>
    {download && !running && !isError && <QueryCsvAction {...download} />}
  </Group>
);
